<script setup lang="ts">
/**
 * Barre de recherche + filtres de l'espace « Mes factures » (DEV-800).
 *
 * Régime invoice-only : aucun verdict/anomalie. Pilote uniquement la requête
 * `FacturesQuery` (miroir des params serveur DEV-799) ; ne fait AUCUN appel
 * réseau ni navigation — le parent câble `store.fetchList(query)`.
 *
 * - Recherche plein-texte, champ unique, debounce ~300 ms, vidable → `q`.
 * - Fournisseur (options = `agregats.fournisseurs`) → `fournisseur`.
 * - Période-année (dérivée de `agregats.periode`) → `depuis`/`jusqua`.
 * - Statut (compteurs depuis `agregats`) → `statut`.
 * - Réinitialiser (conserve le tri par défaut et un éventuel `pdl`).
 */
import { computed, ref, watch } from "vue"
import { useDebounceFn } from "@vueuse/core"
import type {
    FactureVisibilite,
    FacturesAgregats,
    FacturesQuery,
} from "@modules/courtage/types/facture-espace"

defineOptions({ name: "FacturesFiltres" })

const props = defineProps<{
    /** Requête courante (v-model). */
    modelValue: FacturesQuery
    /** Agrégats calculés sur le filtre (peuplent options ET compteurs). */
    agregats: FacturesAgregats | null
}>()

const emit = defineEmits<{
    (e: "update:modelValue", value: FacturesQuery): void
}>()

/** Applique un patch partiel sur la requête courante. */
function patch(changes: Partial<FacturesQuery>): void {
    emit("update:modelValue", { ...props.modelValue, ...changes })
}

// ── Recherche (debounce 300 ms) ──────────────────────────────────────────────
const searchText = ref(props.modelValue.q ?? "")
watch(
    () => props.modelValue.q,
    (v) => {
        if ((v ?? "") !== searchText.value) searchText.value = v ?? ""
    }
)
const debouncedSearch = useDebounceFn((val: string) => {
    const trimmed = val.trim()
    patch({ q: trimmed || undefined })
}, 300)
function onSearchInput(): void {
    void debouncedSearch(searchText.value)
}
function clearSearch(): void {
    searchText.value = ""
    patch({ q: undefined })
}

// ── Fournisseur ──────────────────────────────────────────────────────────────
const fournisseurOptions = computed<string[]>(
    () => props.agregats?.fournisseurs ?? []
)
function onFournisseur(e: Event): void {
    const v = (e.target as HTMLSelectElement).value
    patch({ fournisseur: v || undefined })
}

// ── Période (année) ──────────────────────────────────────────────────────────
const selectedAnnee = computed<number | null>(() =>
    props.modelValue.depuis ? Number(props.modelValue.depuis.slice(0, 4)) : null
)
const anneeOptions = computed<number[]>(() => {
    const years = new Set<number>()
    const p = props.agregats?.periode
    const min = p?.[0] ? Number(p[0].slice(0, 4)) : null
    const max = p?.[1] ? Number(p[1].slice(0, 4)) : null
    if (min && max) for (let y = max; y >= min; y--) years.add(y)
    else if (min) years.add(min)
    else if (max) years.add(max)
    // Garde l'année sélectionnée même si le filtre courant l'a fait sortir.
    if (selectedAnnee.value) years.add(selectedAnnee.value)
    return Array.from(years).sort((a, b) => b - a)
})
function onAnnee(e: Event): void {
    const v = (e.target as HTMLSelectElement).value
    if (!v) patch({ depuis: undefined, jusqua: undefined })
    else patch({ depuis: `${v}-01-01`, jusqua: `${v}-12-31` })
}

// ── Statut (compteurs depuis agrégats) ───────────────────────────────────────
type StatutOption = {
    value: FactureVisibilite | undefined
    label: string
    count: number
}
const statutOptions = computed<StatutOption[]>(() => [
    { value: undefined, label: "Tous", count: props.agregats?.n_total ?? 0 },
    {
        value: "visible",
        label: "À vous",
        count: props.agregats?.n_visible ?? 0,
    },
    {
        value: "a_ajouter",
        label: "À ajouter",
        count: props.agregats?.n_a_ajouter ?? 0,
    },
    {
        value: "a_rattacher",
        label: "À rattacher",
        count: props.agregats?.n_a_rattacher ?? 0,
    },
])
function onStatut(v: FactureVisibilite | undefined): void {
    patch({ statut: v })
}

// ── Réinitialiser ────────────────────────────────────────────────────────────
const hasActiveFilters = computed<boolean>(() => {
    const q = props.modelValue
    return Boolean(q.q || q.fournisseur || q.statut || q.depuis || q.jusqua)
})
function reset(): void {
    searchText.value = ""
    emit("update:modelValue", {
        pdl: props.modelValue.pdl,
        sort: "date",
        order: "desc",
    })
}
</script>

<template>
    <div class="mb-4 rounded-lg border border-neutral-200 bg-white p-4">
        <div class="flex flex-wrap items-center gap-3">
            <!-- Recherche plein-texte -->
            <div class="relative min-w-[14rem] flex-1">
                <i
                    class="pi pi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400"
                    aria-hidden="true"
                />
                <input
                    v-model="searchText"
                    type="search"
                    inputmode="search"
                    aria-label="Rechercher une facture"
                    placeholder="Rechercher (n° facture, compteur, fournisseur, adresse)…"
                    class="w-full rounded-md border border-neutral-300 bg-white py-2 pl-9 pr-9 text-sm text-neutral-800 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    @input="onSearchInput"
                />
                <button
                    v-if="searchText"
                    type="button"
                    aria-label="Effacer la recherche"
                    class="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                    @click="clearSearch"
                >
                    <i class="pi pi-times text-xs" aria-hidden="true" />
                </button>
            </div>

            <!-- Fournisseur -->
            <select
                :value="modelValue.fournisseur ?? ''"
                aria-label="Filtrer par fournisseur"
                class="rounded-md border border-neutral-300 bg-white py-2 pl-3 pr-8 text-sm text-neutral-800 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                @change="onFournisseur"
            >
                <option value="">Tous les fournisseurs</option>
                <option v-for="f in fournisseurOptions" :key="f" :value="f">
                    {{ f }}
                </option>
            </select>

            <!-- Période (année) -->
            <select
                :value="selectedAnnee ?? ''"
                aria-label="Filtrer par année"
                class="rounded-md border border-neutral-300 bg-white py-2 pl-3 pr-8 text-sm text-neutral-800 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                @change="onAnnee"
            >
                <option value="">Toutes les années</option>
                <option v-for="y in anneeOptions" :key="y" :value="y">
                    {{ y }}
                </option>
            </select>

            <!-- Réinitialiser -->
            <button
                type="button"
                :disabled="!hasActiveFilters"
                class="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                @click="reset"
            >
                <i class="pi pi-refresh text-xs" aria-hidden="true" />
                Réinitialiser
            </button>
        </div>

        <!-- Statut : segmenté, chaque option affiche son compteur de résultats -->
        <div
            class="mt-3 flex flex-wrap gap-2"
            role="group"
            aria-label="Filtrer par statut"
        >
            <button
                v-for="opt in statutOptions"
                :key="opt.label"
                type="button"
                :aria-pressed="modelValue.statut === opt.value"
                class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
                :class="
                    modelValue.statut === opt.value
                        ? 'border-primary-800 bg-primary-800 text-white'
                        : 'border-neutral-300 bg-white text-neutral-600 hover:border-neutral-400'
                "
                @click="onStatut(opt.value)"
            >
                {{ opt.label }}
                <span
                    class="rounded-full px-1.5 py-0.5 text-[0.65rem] tabular-nums"
                    :class="
                        modelValue.statut === opt.value
                            ? 'bg-white/20 text-white'
                            : 'bg-neutral-100 text-neutral-500'
                    "
                >
                    {{ opt.count }}
                </span>
            </button>
        </div>
    </div>
</template>
